import { FormEvent, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogIn, Loader2 } from 'lucide-react';
import DotsBackground from '../components/DotsBackground';
import LoginHeader from '../components/LoginHeader';
import { useAuth } from '../contexts/AuthContext';
import './LoginPage.css';

const AUDIO_AGENT_TAGLINES = [
  'Every Track.',
  'Every Mix.',
  'Every Master.',
  'Every Key.',
  'Every BPM.',
  'Every Genre.',
  'Everything Audio.',
];

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = (location.state as { from?: string } | null)?.from || '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await login(email.trim(), password);
      navigate(redirectTo, { replace: true });
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Sign in failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <DotsBackground />
      <LoginHeader taglines={AUDIO_AGENT_TAGLINES} />
      <div className="login-container">
        <p className="login-eyebrow">
          <span className="login-eyebrow-dot" />
          KNKT · Audio Agent
        </p>
        <h1 className="login-title">
          Sign in to <span className="login-title-italic">analyze tracks.</span>
        </h1>
        <p className="login-subtitle">
          Use your KNKT account — the same login as the rest of the platform.
        </p>

        <form className="login-form" onSubmit={handleSubmit}>
          <label className="login-field">
            <span className="login-label">Email</span>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@knkt.io"
              className="login-input"
            />
          </label>

          <label className="login-field">
            <span className="login-label">Password</span>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="login-input"
            />
          </label>

          {error && <div className="login-error">{error}</div>}

          <button type="submit" className="login-submit" disabled={submitting}>
            {submitting ? <Loader2 size={14} className="login-spin" /> : <LogIn size={14} strokeWidth={2} />}
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}
